const INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="combobox"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="slider"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="textbox"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const IGNORED_SELECTOR =
  '[data-finalish-overlay],[data-finalish-ignore],script,style,meta,link';

export function reviewTargetFromEvent(
  event: Event,
  overlayHost?: Element,
): Element | null {
  if (overlayHost && event.composedPath().includes(overlayHost)) return null;
  const rawTarget = event
    .composedPath()
    .find((entry): entry is Element => entry instanceof Element);
  return rawTarget ? normalizeReviewTarget(rawTarget) : null;
}

export function blurActivePageControl(): void {
  const active = document.activeElement;
  if (
    active instanceof HTMLElement &&
    active.matches(
      'input,select,textarea,button,[contenteditable]:not([contenteditable="false"])',
    )
  ) {
    active.blur();
  }
}

export function normalizeReviewTarget(element: Element): Element | null {
  if (element.closest(IGNORED_SELECTOR)) return null;
  const interactive = element.closest(INTERACTIVE_SELECTOR);
  return interactive && !interactive.closest(IGNORED_SELECTOR)
    ? interactive
    : element;
}

export function elementDisplayName(element: Element): string {
  const tag = element.tagName.toLowerCase();
  const type =
    element instanceof HTMLInputElement && element.type
      ? `[type="${element.type}"]`
      : '';
  const role = element.getAttribute('role');
  const identity =
    element.getAttribute('data-finalish-id') ??
    element.getAttribute('data-testid') ??
    element.id;
  const name = isSensitiveElement(element)
    ? null
    : normalizeText(
        element.getAttribute('aria-label') ??
          (isTextualControl(element) ? null : element.textContent),
        48,
      );

  let result = `<${tag}${type}${role ? ` role="${role}"` : ''}>`;
  if (identity) result += ` #${identity}`;
  if (name) result += ` · ${name}`;
  return result;
}

export function isSensitiveElement(element: Element): boolean {
  if (element.closest('[data-finalish-private],[data-private]')) return true;
  if (
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    element.matches('[contenteditable]:not([contenteditable="false"])')
  ) {
    return true;
  }
  if (!(element instanceof HTMLInputElement)) return false;
  return ![
    'button',
    'checkbox',
    'color',
    'file',
    'image',
    'radio',
    'range',
    'reset',
    'submit',
  ].includes(element.type);
}

function isTextualControl(element: Element): boolean {
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    element.matches('[contenteditable]:not([contenteditable="false"])')
  );
}

function normalizeText(value: string | null, maxLength: number): string | null {
  const normalized = value?.replace(/\s+/g, ' ').trim().slice(0, maxLength);
  return normalized || null;
}
